import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faSearch,
    faRefresh,
    faMoneyBillWave,
    faSpinner,
    faExchangeAlt,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faFilePdf,
    faUser,
    faCalendar,
    faPrint,
    faDownload,
    faEye,
    faPlusCircle
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
// import "../styles/SalaryList.css";

// Toast Notification Component
const ToastNotification = ({ toast, setToast }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return 'Success';
            case 'error':
                return 'Error';
            case 'warning':
                return 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content shop-selector-modal">
                <div className="modal-header">
                    <h3>Switch Shop</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>Loading shops...</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>No shops available</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faExchangeAlt} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>Current</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const SalaryList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [salaries, setSalaries] = useState([]);
    const [filteredSalaries, setFilteredSalaries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSalary, setSelectedSalary] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [salaryToDelete, setSalaryToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [showPayVoucher, setShowPayVoucher] = useState(false);
    const [voucherData, setVoucherData] = useState(null);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Timer reference for cleanup
    const toastTimerRef = useRef(null);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        basic_salary: '',
        allowances: '',
        deductions: '',
        bonus: '',
        overtime_hours: '',
        overtime_amount: '',
        commission_amount: '',
        net_salary: '',
        payment_method: '',
        payment_status: '',
        payment_date: '',
        transaction_id: '',
        notes: ''
    });

    const [editFormErrors, setEditFormErrors] = useState({});

    // Payment method options
    const paymentMethodOptions = [
        { value: 'cash', label: 'Cash' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'cheque', label: 'Cheque' }
    ];

    // Payment status options
    const paymentStatusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'paid', label: 'Paid' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    // Get theme and language from localStorage
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Filter states
    const [monthFilter, setMonthFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [employeeFilter, setEmployeeFilter] = useState('');

    const totalPages = Math.ceil(filteredSalaries.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentSalaries = filteredSalaries.slice(startIndex, endIndex);

    // Function to show toast
    const showToast = (type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    };

    // Clean up timer
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'salary-primary-color': color,
                    'salary-primary-dark': adjust(-40),
                    'salary-primary-light': adjust(40),
                    'salary-primary-ultralight': adjust(20),
                    'salary-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'salary-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });
            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#6366f1';
            if (theme !== currentTheme) {
                setCurrentTheme(theme);
                applyTheme(theme);
            }
        };

        handleThemeChange();

        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [currentTheme]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        }
    }, [currentLanguage]);

    // Fetch salaries
    useEffect(() => {
        if (user && user.username && currentShop) {
            fetchSalaries();
            fetchAvailableShops();
        } else {
            setLoading(false);
        }
    }, [user, currentShop]);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [salaries, searchTerm, monthFilter, statusFilter, employeeFilter]);

    // Fetch available shops
    const fetchAvailableShops = async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                fetchSalaries();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    };

    const fetchSalaries = async () => {
        if (!user || !user.username || !currentShop) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/employee_salaries.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_salary_records',
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setSalaries(data.data || []);
                if (data.data.length === 0) {
                    showToast('info', 'No salary records found');
                }
            } else {
                showToast('error', data.message || 'Error fetching salaries');
            }
        } catch (error) {
            console.error('Error fetching salaries:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...salaries];

        // Apply search filter
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(salary =>
                salary.employee_name?.toLowerCase().includes(term) ||
                salary.employeeid?.toString().includes(term) ||
                salary.salaryid?.toString().includes(term) ||
                salary.transaction_id?.toLowerCase().includes(term)
            );
        }

        // Apply month filter
        if (monthFilter) {
            filtered = filtered.filter(salary =>
                salary.salary_month?.startsWith(monthFilter)
            );
        }

        // Apply status filter
        if (statusFilter) {
            filtered = filtered.filter(salary =>
                salary.payment_status === statusFilter
            );
        }

        // Apply employee filter
        if (employeeFilter) {
            filtered = filtered.filter(salary =>
                salary.employeeid?.toString() === employeeFilter
            );
        }

        setFilteredSalaries(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setSearchTerm('');
        setMonthFilter('');
        setStatusFilter('');
        setEmployeeFilter('');
    };

    const handleEdit = (salary) => {
        setSelectedSalary(salary);
        setEditFormData({
            basic_salary: salary.basic_salary || '',
            allowances: salary.allowances || '',
            deductions: salary.deductions || '',
            bonus: salary.bonus || '',
            overtime_hours: salary.overtime_hours || '',
            overtime_amount: salary.overtime_amount || '',
            commission_amount: salary.commission_amount || '',
            net_salary: salary.net_salary || '',
            payment_method: salary.payment_method || 'cash',
            payment_status: salary.payment_status || 'pending',
            payment_date: salary.payment_date ? salary.payment_date.split('T')[0] : '',
            transaction_id: salary.transaction_id || '',
            notes: salary.notes || ''
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleDeleteClick = (salaryId) => {
        setSalaryToDelete(salaryId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!salaryToDelete || !currentShop) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/employee_salaries.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete_salary',
                    salaryid: salaryToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', 'Salary record deleted successfully');
                fetchSalaries();
                setShowDeleteModal(false);
                setSalaryToDelete(null);
            } else {
                showToast('error', data.message || 'Error deleting salary record');
            }
        } catch (error) {
            console.error('Error deleting salary:', error);
            showToast('error', 'Error deleting salary record');
        }
    };

    const validateEditForm = () => {
        const newErrors = {};

        if (editFormData.net_salary <= 0) {
            newErrors.net_salary = 'Net salary must be greater than 0';
        }

        setEditFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (editFormErrors[name]) {
            setEditFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateSalary = async (e) => {
        e.preventDefault();

        if (!selectedSalary || !currentShop) return;

        if (!validateEditForm()) {
            return;
        }

        setEditLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/employee_salaries.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'edit_salary',
                    salaryid: selectedSalary.salaryid,
                    ...editFormData,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', 'Salary record updated successfully');
                fetchSalaries();
                setShowEditModal(false);
                setSelectedSalary(null);
                setEditFormErrors({});
            } else {
                showToast('error', data.message || 'Error updating salary record');
            }
        } catch (error) {
            console.error('Error updating salary:', error);
            showToast('error', 'Error updating salary record');
        } finally {
            setEditLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // View pay voucher
    const handleViewVoucher = (salary) => {
        setVoucherData(salary);
        setShowPayVoucher(true);
    };

    // Generate PDF voucher (similar to AddSalary but using existing data)
    const generatePayVoucher = async (salary) => {
        try {
            // This would be similar to the voucher generation in AddSalary
            // You can reuse the same function but with salary data
            showToast('info', 'Voucher generation feature coming soon');
        } catch (error) {
            console.error('Error generating voucher:', error);
            showToast('error', 'Error generating voucher');
        }
    };

    // Get status badge color
    const getStatusBadgeStyle = (status) => {
        const colors = {
            'paid': { backgroundColor: '#10b981', color: '#ffffff' },
            'pending': { backgroundColor: '#f59e0b', color: '#ffffff' },
            'cancelled': { backgroundColor: '#ef4444', color: '#ffffff' }
        };
        return colors[status] || { backgroundColor: '#6b7280', color: '#ffffff' };
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">No shop selected</span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faExchangeAlt} />
                        <span>Switch Shop</span>
                    </button>
                </div>
            );
        }
    };

    return (
        <div className={`salary-list-modern ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                loading={loadingShops}
            />

            {/* Pay Voucher Modal */}
            {showPayVoucher && voucherData && (
                <div className="modal-overlay" onClick={() => setShowPayVoucher(false)}>
                    <div className="modal-content pay-voucher-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Pay Voucher - {voucherData.employee_name}</h3>
                            <button className="modal-close" onClick={() => setShowPayVoucher(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="voucher-actions">
                                <button
                                    className="btn-modern btn-primary-modern"
                                    onClick={() => generatePayVoucher(voucherData)}
                                >
                                    <FontAwesomeIcon icon={faDownload} />
                                    Download PDF
                                </button>
                                <button
                                    className="btn-modern btn-secondary-modern"
                                    onClick={() => window.print()}
                                >
                                    <FontAwesomeIcon icon={faPrint} />
                                    Print
                                </button>
                                <button
                                    className="btn-modern btn-secondary-modern"
                                    onClick={() => setShowPayVoucher(false)}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    Close
                                </button>
                            </div>
                            <div className="voucher-preview">
                                <h4>Voucher #{voucherData.salaryid}</h4>
                                <div className="voucher-details">
                                    <p><strong>Employee:</strong> {voucherData.employee_name}</p>
                                    <p><strong>Month:</strong> {voucherData.salary_month}</p>
                                    <p><strong>Basic Salary:</strong> {currentShop?.currency || '$'}{formatCurrency(voucherData.basic_salary)}</p>
                                    <p><strong>Allowances:</strong> {currentShop?.currency || '$'}{formatCurrency(voucherData.allowances)}</p>
                                    <p><strong>Deductions:</strong> {currentShop?.currency || '$'}{formatCurrency(voucherData.deductions)}</p>
                                    <p><strong>Bonus:</strong> {currentShop?.currency || '$'}{formatCurrency(voucherData.bonus)}</p>
                                    <p><strong>Net Salary:</strong> {currentShop?.currency || '$'}{formatCurrency(voucherData.net_salary)}</p>
                                    <p><strong>Payment Method:</strong> {voucherData.payment_method}</p>
                                    <p><strong>Status:</strong> {voucherData.payment_status}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Header Section */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faMoneyBillWave} />
                    </div>
                    <div className="header-text">
                        <h1>Salary Records</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `Shop: ${currentShop.shop_name}`
                                    : `Salary Records - ${user?.username}`}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Shop Indicator */}
                {renderShopIndicator()}

                {/* Add New Button */}
                <button
                    className="btn-modern btn-primary-modern add-new-btn"
                    onClick={() => window.location.href = '/AddSalary'}
                >
                    <FontAwesomeIcon icon={faPlusCircle} />
                    <span>Add New Salary</span>
                </button>
            </div>

            {/* Filters Section */}
            <div className="filters-section">
                <div className="filters-row">
                    <div className="filter-group">
                        <div className="search-box">
                            <input
                                type="text"
                                placeholder="Search by employee name, ID, transaction..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <FontAwesomeIcon icon={faSearch} className="search-icon" />
                        </div>
                    </div>

                    <div className="filter-group">
                        <input
                            type="month"
                            value={monthFilter}
                            onChange={(e) => setMonthFilter(e.target.value)}
                            placeholder="Filter by month"
                            className="filter-input"
                        />
                    </div>

                    <div className="filter-group">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Status</option>
                            {paymentStatusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <button
                            onClick={fetchSalaries}
                            className="refresh-btn-modern"
                            disabled={loading}
                            title="Refresh"
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">Refresh</span>
                        </button>
                    </div>

                    <div className="filter-group">
                        <button
                            onClick={resetFilters}
                            className="reset-filter-btn"
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                            <span>Clear Filters</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>Loading salary records...</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faExchangeAlt} />
                        <h3>No Shop Selected</h3>
                        <p>Please select a shop to view salary records</p>
                        <button
                            className="btn-modern btn-primary-modern"
                            onClick={() => setShowShopSelector(true)}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>Switch Shop</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="salary-table-wrapper">
                                <table className="salary-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">S.No</th>
                                            <th className="employee-column centered-cell">Employee</th>
                                            <th className="month-column centered-cell">Month</th>
                                            <th className="amount-column centered-cell">Basic</th>
                                            <th className="amount-column centered-cell">Allowances</th>
                                            <th className="amount-column centered-cell">Deductions</th>
                                            <th className="amount-column centered-cell">Bonus</th>
                                            <th className="amount-column centered-cell">Net Salary</th>
                                            <th className="status-column centered-cell">Status</th>
                                            <th className="date-column centered-cell">Payment Date</th>
                                            <th className="actions-column centered-cell">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentSalaries.map((salary, index) => {
                                            const statusStyle = getStatusBadgeStyle(salary.payment_status);

                                            return (
                                                <tr key={salary.salaryid}>
                                                    <td className="id-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="cell-text">{startIndex + index + 1}</span>
                                                        </div>
                                                    </td>
                                                    <td className="employee-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faUser} className="cell-icon" />
                                                            <span className="cell-text">{salary.employee_name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="month-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faCalendar} className="cell-icon" />
                                                            <span className="cell-text">{salary.salary_month}</span>
                                                        </div>
                                                    </td>
                                                    <td className="amount-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="cell-text">{currentShop?.currency || '$'}{formatCurrency(salary.basic_salary)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="amount-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="cell-text">{currentShop?.currency || '$'}{formatCurrency(salary.allowances)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="amount-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="cell-text">{currentShop?.currency || '$'}{formatCurrency(salary.deductions)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="amount-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="cell-text">{currentShop?.currency || '$'}{formatCurrency(salary.bonus)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="amount-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="cell-text net-salary">{currentShop?.currency || '$'}{formatCurrency(salary.net_salary)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="status-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span
                                                                className="status-badge"
                                                                style={statusStyle}
                                                            >
                                                                {salary.payment_status}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="date-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="cell-text">{salary.payment_date || '-'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="actions-cell-modern centered-cell">
                                                        <div className="cell-content centered-content actions-content">
                                                            <button
                                                                onClick={() => handleViewVoucher(salary)}
                                                                className="action-btn-modern view-btn-modern"
                                                                title="View Voucher"
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(salary)}
                                                                className="action-btn-modern edit-btn-modern"
                                                                title="Edit"
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteClick(salary.salaryid)}
                                                                className="action-btn-modern delete-btn-modern"
                                                                title="Delete"
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {/* No Data Message */}
                                {currentSalaries.length === 0 && !loading && currentShop && (
                                    <div className="no-data-modern">
                                        <FontAwesomeIcon icon={faMoneyBillWave} />
                                        <p>No salary records found</p>
                                        <button
                                            className="btn-modern btn-primary-modern"
                                            style={{ marginTop: '20px' }}
                                            onClick={() => window.location.href = '/AddSalary'}
                                        >
                                            <FontAwesomeIcon icon={faPlusCircle} />
                                            <span>Add First Salary Record</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredSalaries.length > 0 && currentShop && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>Showing {startIndex + 1}-{Math.min(endIndex, filteredSalaries.length)} of {filteredSalaries.length} records</span>
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>Previous</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>Next</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedSalary && (
                <div className="modal-overlay-modern edit-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content-modern edit-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="form-header-modern edit-form-header">
                            <div className="header-content">
                                <div className="header-icon">
                                    <FontAwesomeIcon icon={faMoneyBillWave} />
                                </div>
                                <div className="header-text">
                                    <h1>Edit Salary Record</h1>
                                    <p>Update salary details for {selectedSalary.employee_name}</p>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleUpdateSalary} className="modern-form edit-form">
                            <div className="form-grid-modern edit-form-grid">
                                <div className={`form-group-modern ${editFormErrors.net_salary ? 'has-error' : ''}`}>
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                        <input
                                            type="number"
                                            id="net_salary"
                                            name="net_salary"
                                            value={editFormData.net_salary}
                                            onChange={handleEditFormChange}
                                            required
                                            placeholder="Net Salary"
                                            disabled={editLoading}
                                            step="0.01"
                                            min="0"
                                        />
                                        <label htmlFor="net_salary" className="input-label">
                                            Net Salary
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.net_salary && (
                                        <div className="error-message-modern">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.net_salary}
                                        </div>
                                    )}
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="input-icon" />
                                        <select
                                            id="payment_method"
                                            name="payment_method"
                                            value={editFormData.payment_method}
                                            onChange={handleEditFormChange}
                                            disabled={editLoading}
                                        >
                                            {paymentMethodOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <label htmlFor="payment_method" className="input-label">
                                            Payment Method
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faCheckCircle} className="input-icon" />
                                        <select
                                            id="payment_status"
                                            name="payment_status"
                                            value={editFormData.payment_status}
                                            onChange={handleEditFormChange}
                                            disabled={editLoading}
                                        >
                                            {paymentStatusOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <label htmlFor="payment_status" className="input-label">
                                            Payment Status
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faCalendar} className="input-icon" />
                                        <input
                                            type="date"
                                            id="payment_date"
                                            name="payment_date"
                                            value={editFormData.payment_date}
                                            onChange={handleEditFormChange}
                                            disabled={editLoading}
                                        />
                                        <label htmlFor="payment_date" className="input-label">
                                            Payment Date
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faFilePdf} className="input-icon" />
                                        <input
                                            type="text"
                                            id="transaction_id"
                                            name="transaction_id"
                                            value={editFormData.transaction_id}
                                            onChange={handleEditFormChange}
                                            placeholder="Transaction ID"
                                            disabled={editLoading}
                                        />
                                        <label htmlFor="transaction_id" className="input-label">
                                            Transaction ID
                                        </label>
                                    </div>
                                </div>

                                <div className="form-group-modern full-width">
                                    <div className="input-container">
                                        <FontAwesomeIcon icon={faFilePdf} className="input-icon" />
                                        <textarea
                                            id="notes"
                                            name="notes"
                                            value={editFormData.notes}
                                            onChange={handleEditFormChange}
                                            rows="3"
                                            placeholder="Additional notes"
                                            disabled={editLoading}
                                        />
                                        <label htmlFor="notes" className="input-label">
                                            Notes
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="form-actions-modern edit-form-actions">
                                <button
                                    type="button"
                                    className="btn-modern btn-secondary-modern"
                                    onClick={() => setShowEditModal(false)}
                                    disabled={editLoading}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                    <span>Cancel</span>
                                </button>
                                <button
                                    type="submit"
                                    className="btn-modern btn-primary-modern"
                                    disabled={editLoading}
                                >
                                    {editLoading ? (
                                        <>
                                            <div className="btn-spinner"></div>
                                            <span>Updating...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Save Changes</span>
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>

                        <div className="edit-salary-info">
                            <p><strong>Employee:</strong> {selectedSalary.employee_name}</p>
                            <p><strong>Month:</strong> {selectedSalary.salary_month}</p>
                            <p><strong>Salary ID:</strong> #{selectedSalary.salaryid}</p>
                        </div>

                        {editLoading && (
                            <div className="loading-overlay edit-loading-overlay">
                                <div className="loading-content">
                                    <div className="loading-spinner"></div>
                                    <p>Updating salary record...</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                            <div className="modal-title">
                                <h3 style={{ color: 'var(--salary-error-color)' }}>
                                    Confirm Delete
                                </h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--salary-text-primary)' }}>
                                    Are you sure you want to delete this salary record?
                                </p>
                                <p style={{ fontSize: '0.9rem', color: 'var(--salary-warning-color)' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                                    This action cannot be undone.
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                type="button"
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                No, Cancel
                            </button>
                            <button
                                type="button"
                                className="btn-modern btn-delete-modern"
                                onClick={handleDelete}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                Yes, Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalaryList;